'use client'

import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { useState, useCallback, useEffect, useRef } from 'react'
import { Input } from '@/components/common/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/select'
import { cn } from '@/lib/utils/utils'

interface Location {
  id: string
  displayName: string
}

interface DepartmentOption {
  id: string
  name: string
}

interface JobFiltersProps {
  locations?: Location[]
  showLocationFilter?: boolean
  departmentOptions?: DepartmentOption[]
  showDepartmentFilters?: boolean
}

export function JobFilters({
  locations = [],
  showLocationFilter = true,
  departmentOptions = [],
  showDepartmentFilters = false,
}: JobFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const [isNavigating, setIsNavigating] = useState(false)

  // Get current values from URL (source of truth)
  const urlQuery = searchParams.get('q') || ''
  const urlLocation = searchParams.get('location') || 'all'
  // `department` may appear multiple times — collect all values into an array.
  const urlDepartments = searchParams.getAll('department')

  // Use local state for controlled inputs, initialized from URL
  const [searchQuery, setSearchQuery] = useState(urlQuery)
  const [selectedLocation, setSelectedLocation] = useState(urlLocation)
  const [selectedDepartments, setSelectedDepartments] =
    useState<string[]>(urlDepartments)

  // Track if we're updating URL to prevent sync loops
  const isUpdatingURL = useRef(false)

  // Track the "target" URL so we can clear the spinner as soon as navigation lands.
  const pendingPathnameRef = useRef<string | null>(null)
  const pendingQueryStringRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isNavigating) return

    const pendingPathname = pendingPathnameRef.current
    const pendingQueryString = pendingQueryStringRef.current
    if (pendingPathname == null || pendingQueryString == null) return

    const currentQueryString = searchParams.toString()

    if (
      pathname === pendingPathname &&
      currentQueryString === pendingQueryString
    ) {
      pendingPathnameRef.current = null
      pendingQueryStringRef.current = null
      isUpdatingURL.current = false
      setIsNavigating(false)
    }
  }, [isNavigating, pathname, searchParams])

  // Sync local state with URL only when URL changes externally (e.g., browser back/forward)
  useEffect(() => {
    if (isUpdatingURL.current) {
      isUpdatingURL.current = false
      return
    }
    if (urlQuery !== searchQuery) {
      setSearchQuery(urlQuery)
    }
    if (urlLocation !== selectedLocation) {
      setSelectedLocation(urlLocation)
    }
    // Compare department arrays by joined string for shallow equality
    const urlDeptsKey = urlDepartments.join(',')
    const localDeptsKey = selectedDepartments.join(',')
    if (urlDeptsKey !== localDeptsKey) {
      setSelectedDepartments(urlDepartments)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, urlLocation, urlDepartments.join(',')])

  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const updateURL = useCallback(
    (updates: {
      q?: string
      location?: string
      departments?: string[]
      page?: string
    }) => {
      isUpdatingURL.current = true

      const params = new URLSearchParams(searchParamsRef.current.toString())

      if (updates.q !== undefined) {
        const trimmedQuery = updates.q.trim()
        if (trimmedQuery) {
          params.set('q', trimmedQuery)
        } else {
          params.delete('q')
        }
      }

      if (updates.location !== undefined) {
        if (updates.location && updates.location !== 'all') {
          params.set('location', updates.location)
        } else {
          params.delete('location')
        }
      }

      if (updates.departments !== undefined) {
        params.delete('department')
        for (const d of updates.departments) {
          params.append('department', d)
        }
      }

      // Reset to page 1 when filters change
      if (updates.page === undefined) {
        params.delete('page')
      } else if (updates.page !== '1') {
        params.set('page', updates.page)
      }

      const queryString = params.toString()
      const url = queryString ? `${pathname}?${queryString}` : pathname

      pendingPathnameRef.current = pathname
      pendingQueryStringRef.current = queryString
      setIsNavigating(true)
      router.replace(url, { scroll: false })
    },
    [router, pathname]
  )

  // Handle search input with debounce.
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentUrlQuery = searchParamsRef.current.get('q') || ''
      const trimmedQuery = searchQuery.trim()
      if (trimmedQuery !== currentUrlQuery) {
        updateURL({ q: trimmedQuery })
      }
    }, 250)

    return () => clearTimeout(timer)
  }, [searchQuery, updateURL])

  const handleLocationChange = (value: string) => {
    setSelectedLocation(value)
    updateURL({ location: value })
  }

  const toggleDepartment = (deptId: string) => {
    const next = selectedDepartments.includes(deptId)
      ? selectedDepartments.filter((d) => d !== deptId)
      : [...selectedDepartments, deptId]
    setSelectedDepartments(next)
    updateURL({ departments: next })
  }

  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-[298px] items-start w-full">
        <h1 className="leading-[normal] text-primary-blue text-[28px] lg:text-[36px] w-full lg:w-[534px] whitespace-pre-wrap">
          Find your dream role
        </h1>
        <div className="flex flex-col sm:flex-row flex-1 gap-4 items-start w-full lg:w-auto">
          <div className="flex-1 relative w-full sm:min-w-48">
            <Input
              type="text"
              placeholder="Search jobs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="py-4 px-6 rounded-[14px] border-primary-blue text-[18px] w-full"
            />
            {isNavigating && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="size-4 animate-spin rounded-full border-2 border-primary-blue border-t-transparent" />
              </div>
            )}
          </div>
          {showLocationFilter && (
            <Select
              value={selectedLocation}
              onValueChange={handleLocationChange}
            >
              <SelectTrigger className="h-[46px] px-6 py-[9px] rounded-[14px] border-primary-blue text-[18px] w-full sm:w-auto sm:min-w-[180px]">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {showDepartmentFilters && departmentOptions.length > 0 && (
        <div className="grid grid-cols-2 gap-3 w-full lg:flex lg:flex-wrap lg:gap-4 lg:justify-end">
          {departmentOptions.map((option) => {
            const isSelected = selectedDepartments.includes(option.id)
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => toggleDepartment(option.id)}
                aria-pressed={isSelected}
                className={cn(
                  'min-h-[46px] px-6 py-[9px] rounded-[14px] text-[18px] text-center leading-[26px] transition-colors lg:h-[46px] lg:whitespace-nowrap',
                  isSelected
                    ? 'bg-careers-pink text-primary-blue'
                    : 'bg-primary-blue text-white hover:bg-primary-blue/90'
                )}
              >
                {option.name}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
